import { memo, useState, useRef, useEffect } from "react";
import { Grid2, Typography, Avatar, Button, Alert } from "@mui/material";
import { icon } from "@/app/style/icons";
import SettingsCard from "@settings/base/SettingsCard";
import SettingsFormField from "@settings/components/forms/SettingsFormField";
import { FETCH_USER_PROFILE } from "@company/graphql/queries/fetchUserProfile";
import { Mutation, MutationUpdateUserArgs, MutationChangePasswordArgs, MutationChangeEmailArgs, Query, User } from "@/app/graphql/codegen";
import { useMutation, useQuery } from "@apollo/client";
import BaseSettingsPage from "@settings/base/BaseSettingsPage";
import { UPDATE_USER_PROFILE } from "@company/graphql/mutations/updateUserProfile";
import { CHANGE_PASSWORD } from "@auth/graphql/mutations/changePassword";
import { CHANGE_EMAIL } from "@auth/graphql/mutations/changeEmail";
import { ProfilePictureCard } from "../../components/cards/ProfilePictureCard";
import { ProfileInfoCard } from "../../components/cards/ProfileInfoCard";
import { ProfileValidationData, validateProfileData } from "@/utils/validateProfileData";
import { useDispatch } from "react-redux";
import { handleErrorWithNotification, showSuccessNotification } from "@/utils/errorHandling";
import { EmailVerificationBanner } from "@auth/components/EmailVerificationBanner";

const ProfileSettingsPage = memo(() => {
    const { data, loading } = useQuery<Pick<Query, "userProfile">, User>(FETCH_USER_PROFILE);
    const dispatch = useDispatch();
    const profile = data?.userProfile;

    // Formulardaten
    const [profileData, setProfileData] = useState<ProfileValidationData>({
        name: "",
        title: "",
    });

    const [passwordData, setPasswordData] = useState({
        oldPassword: "",
        newPassword: "",
        confirmPassword: "",
    });

    const [emailData, setEmailData] = useState({
        newEmail: "",
    });

    // Error states
    const [profileErrors, setProfileErrors] = useState<Record<string, string>>({});
    const [passwordErrors, setPasswordErrors] = useState<Record<string, string>>({});
    const [emailErrors, setEmailErrors] = useState<Record<string, string>>({});

    // Mutations
    const [updateProfileMutation] = useMutation<Pick<Mutation, "updateUser">, MutationUpdateUserArgs>(UPDATE_USER_PROFILE);
    const [changePasswordMutation] = useMutation<Pick<Mutation, "changePassword">, MutationChangePasswordArgs>(CHANGE_PASSWORD);
    const [changeEmailMutation] = useMutation<Pick<Mutation, "changeEmail">, MutationChangeEmailArgs>(CHANGE_EMAIL);

    // Initialize form data when profile loads
    useEffect(() => {
        if (profile) {
            setProfileData({
                name: profile.name || "",
                title: profile.title || "",
            });
            setEmailData({ newEmail: profile.email || "" });
        }
    }, [profile]);

    const validatePasswordData = () => {
        const errors: Record<string, string> = {};

        if (!passwordData.oldPassword) {
            errors.oldPassword = "Bitte geben Sie Ihr aktuelles Passwort ein";
        }
        if (!passwordData.newPassword) {
            errors.newPassword = "Bitte geben Sie ein neues Passwort ein";
        }
        if (passwordData.newPassword !== passwordData.confirmPassword) {
            errors.confirmPassword = "Die Passwörter stimmen nicht überein";
        }

        setPasswordErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const validateEmailData = () => {
        const errors: Record<string, string> = {};

        if (!emailData.newEmail) {
            errors.newEmail = "Bitte geben Sie eine neue E-Mail-Adresse ein";
        }
        // Basic email validation
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailData.newEmail)) {
            errors.newEmail = "Bitte geben Sie eine gültige E-Mail-Adresse ein";
        }

        setEmailErrors(errors);
        return Object.keys(errors).length === 0;
    };

    // Save handlers
    const handleSaveProfile = async (): Promise<boolean> => {
        try {
            // Validiere die Daten und erhalte mögliche Fehler
            const validationErrors = validateProfileData(profileData);
            setProfileErrors(validationErrors);

            // Falls es Validierungsfehler gibt oder keine Profil-ID, breche ab und gib false zurück
            if (Object.keys(validationErrors).length > 0 || !profile?.id) {
                return false;
            }

            // Führe die Mutation aus
            const result = await updateProfileMutation({
                variables: {
                    id: profile.id,
                    name: profileData.name,
                    title: profileData.title,
                },
                refetchQueries: [{ query: FETCH_USER_PROFILE }],
            });

            if (result) {
                showSuccessNotification("Dein Profil wurde erfolgreich geändert", dispatch, "Profil geändert");
            }

            // Prüfe, ob die Mutation erfolgreich war
            return !!result.data?.updateUser;
        } catch (error) {
            // Setze Fehler im UI
            setProfileErrors({ submit: "Fehler beim Speichern der Profildaten." });

            // Zeige Fehlerbenachrichtigung
            handleErrorWithNotification(error as Error, dispatch, "Fehler", "Die Profileinstellungen konnten nicht gespeichert werden");

            // Gib false zurück, um anzuzeigen, dass das Speichern fehlgeschlagen ist
            return false;
        }
    };

    const handleChangePassword = async () => {
        try {
            if (!validatePasswordData()) return;

            await changePasswordMutation({
                variables: {
                    oldPassword: passwordData.oldPassword,
                    newPassword: passwordData.newPassword,
                },
            });

            setPasswordData({ oldPassword: "", newPassword: "", confirmPassword: "" });

            // Erfolgsbenachrichtigung anzeigen
            showSuccessNotification("Ihr Passwort wurde erfolgreich geändert", dispatch, "Passwort geändert");
        } catch (error) {
            setPasswordErrors({ submit: "Fehler beim Ändern des Passworts." });

            // Fehlerbenachrichtigung anzeigen
            handleErrorWithNotification(error as Error, dispatch, "Fehler", "Das Passwort konnte nicht geändert werden");
        }
    };

    const handleChangeEmail = async () => {
        try {
            if (!validateEmailData()) return;

            await changeEmailMutation({
                variables: {
                    newEmail: emailData.newEmail,
                },
            });

            // Erfolgsbenachrichtigung anzeigen
            showSuccessNotification("Ihre E-Mail-Adresse wurde erfolgreich geändert", dispatch, "E-Mail geändert");
        } catch (error) {
            setEmailErrors({ submit: "Fehler beim Ändern der E-Mail-Adresse." });

            // Fehlerbenachrichtigung anzeigen
            handleErrorWithNotification(error as Error, dispatch, "Fehler", "Die E-Mail-Adresse konnte nicht geändert werden");
        }
    };

    const hasChanges =
        JSON.stringify(profileData) !==
        JSON.stringify({
            name: profile?.name || "",
            title: profile?.title || "",
        });

    return (
        <BaseSettingsPage
            title="Profileinstellungen"
            // onSave={handleSaveProfile}
            saveDisabled={!hasChanges || loading}
            successMessage="Ihre Profileinstellungen wurden erfolgreich gespeichert"
            hasChanges={hasChanges}
        >
            <Grid2 container size={12} spacing={4} p={1.5} pr={3}>
                {/* Show verification banner if email is not verified */}
                {profile && !profile.emailVerified && (
                    <Grid2 size={12}>
                        <EmailVerificationBanner userEmail={profile.email} />
                    </Grid2>
                )}

                <Grid2 size={12}>
                    <ProfileInfoCard profileData={profileData} profileErrors={profileErrors} setProfileData={setProfileData} onSave={handleSaveProfile} />
                </Grid2>

                {/* Login Details Card */}
                <SettingsCard title="E-Mail-Adresse ändern">
                    <Grid2 container spacing={3}>
                        {/* Email Change Section */}
                        <Grid2 sx={{ xs: 12 }}>
                            {Object.keys(emailErrors).length > 0 && (
                                <Alert severity="error" sx={{ mb: 2 }}>
                                    {Object.values(emailErrors).map((error, index) => (
                                        <Typography key={index}>{error}</Typography>
                                    ))}
                                </Alert>
                            )}
                            <SettingsFormField
                                label="E-Mail-Adresse"
                                type="email"
                                value={emailData.newEmail}
                                onChange={(e) => setEmailData({ newEmail: e.target.value })}
                                error={emailErrors.newEmail}
                            />
                            <Button variant="contained" color="primary" onClick={handleChangeEmail} sx={{ mt: 1 }}>
                                E-Mail-Adresse ändern
                            </Button>
                        </Grid2>
                    </Grid2>
                </SettingsCard>

                <SettingsCard title="Passwort ändern">
                    <Grid2 container spacing={3}>
                        {/* Password Change Section */}
                        <Grid2 sx={{ mt: 3, xs: 12 }}>
                            {Object.keys(passwordErrors).length > 0 && (
                                <Alert severity="error" sx={{ mb: 2 }}>
                                    {Object.values(passwordErrors).map((error, index) => (
                                        <Typography key={index}>{error}</Typography>
                                    ))}
                                </Alert>
                            )}
                            <Grid2 container spacing={2}>
                                <SettingsFormField
                                    label="Aktuelles Passwort"
                                    type="password"
                                    value={passwordData.oldPassword}
                                    onChange={(e) => setPasswordData((prev) => ({ ...prev, oldPassword: e.target.value }))}
                                    error={passwordErrors.oldPassword}
                                />
                                <SettingsFormField
                                    label="Neues Passwort"
                                    type="password"
                                    value={passwordData.newPassword}
                                    onChange={(e) => setPasswordData((prev) => ({ ...prev, newPassword: e.target.value }))}
                                    error={passwordErrors.newPassword}
                                />
                                <SettingsFormField
                                    label="Neues Passwort bestätigen"
                                    type="password"
                                    value={passwordData.confirmPassword}
                                    onChange={(e) => setPasswordData((prev) => ({ ...prev, confirmPassword: e.target.value }))}
                                    error={passwordErrors.confirmPassword}
                                />
                            </Grid2>
                            <Button variant="contained" color="primary" onClick={handleChangePassword} sx={{ mt: 1 }}>
                                Passwort ändern
                            </Button>
                        </Grid2>
                    </Grid2>
                </SettingsCard>
            </Grid2>
        </BaseSettingsPage>
    );
});

export default ProfileSettingsPage;
